/** API-only ship-change helpers. Never invent legs or counts. */

import { formatEtaDate, predictedEta, toDayMs } from './shipmentSchedule.js';

/** ShipsGo DISCHARGED only (our status=arrived). Still at_port ≠ off the ship. */
export function cargoIsFullyDischarged(row) {
  if (!row || typeof row !== 'object') return false;
  const st = String(row.status || row.status_shipsgo || '').toLowerCase();
  return st === 'arrived' || st === 'discharged';
}

/** At POD: ARRIVED (at_port) or DISCHARGED (arrived). Status only, not 100% transit. */
export function cargoIsDischarged(row) {
  if (!row || typeof row !== 'object') return false;
  if (cargoIsFullyDischarged(row)) return true;
  const st = String(row.status || row.status_shipsgo || '').toLowerCase();
  return st === 'at_port';
}

export function asJsonArray(raw) {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
}



export function parseShipChanges(shipment) {
  return asJsonArray(shipment?.ship_changes).map((c) => ({
    at_port: c?.at_port || '',
    at_date: c?.at_date || null,
    from_vessel: c?.from_vessel || '',
    from_imo: c?.from_imo || '',
    to_vessel: c?.to_vessel || '',
    to_imo: c?.to_imo || '',
  }));
}

export function parseVesselLegs(shipment) {
  return asJsonArray(shipment?.vessel_legs);
}

export function displayVessel(name) {
  return String(name || '').trim().split(/\s+/).filter(Boolean).map((word) => {
    if (/^[A-Z]{2,3}$/.test(word)) return word;
    if (word === word.toUpperCase() && /[A-Z]/.test(word)) {
      return word.charAt(0) + word.slice(1).toLowerCase();
    }
    return word;
  }).join(' ');
}

export function shortPort(port) {
  const raw = String(port || '').trim();
  if (!raw) return '';
  const inner = raw.match(/\(([^)]+)\)/);
  return displayVessel(inner ? inner[1] : raw);
}

function vesselKey(name) {
  return String(name || '').trim().replace(/\s+/g, ' ').toUpperCase();
}

/** Feeders converge on current vessel, then split to destination ports. */
export function buildVesselBoardFlow(shipment) {
  const groups = Array.isArray(shipment?.tracked_groups) ? shipment.tracked_groups : [];
  const hubRaw = String(shipment?.current_vessel || shipment?.vessel || '').trim();
  const nowKey = vesselKey(hubRaw);
  const feeders = new Map();
  const dests = new Map();
  groups.forEach((g) => {
    const boxes = Number(g?.total_containers);
    const n = Number.isFinite(boxes) ? boxes : 0;
    const feeder = String(g?.booking_vessel || '').trim();
    const fk = vesselKey(feeder);
    if (feeder && fk !== nowKey) {
      const prev = feeders.get(fk) || { name: displayVessel(feeder), n: 0 };
      prev.n += n;
      feeders.set(fk, prev);
    }
    const portRaw = String(g?.arrival_port || '').trim();
    if (!portRaw) return;
    const pk = portKey(portRaw);
    const d = dests.get(pk) || {
      port: shortPort(portRaw).replace(/^Port /i, ''),
      n: 0,
      delay: null,
    };
    d.n += n;
    const delay = Number(g?.delay_days ?? g?.delayDays);
    if (Number.isFinite(delay) && (d.delay == null || delay > d.delay)) d.delay = delay;
    dests.set(pk, d);
  });
  if (!feeders.size && !dests.size) return null;
  // Same ETA order as cargoPodStops (Stop 1/2), not tracked_groups insertion order.
  const etaByPort = new Map();
  for (const stop of asJsonArray(shipment?.voyage_stops)) {
    const key = portKey(stop?.arrival_port);
    if (!key || etaByPort.has(key)) continue;
    etaByPort.set(key, toDayMs(predictedEta(stop)));
  }
  const etaFor = (destKey) => {
    if (etaByPort.has(destKey)) return etaByPort.get(destKey);
    for (const [k, ms] of etaByPort) {
      if (destKey.startsWith(`${k} `) || k.startsWith(`${destKey} `)) return ms;
    }
    return null;
  };
  const destinations = [...dests.entries()]
    .sort(([ka], [kb]) => {
      const am = etaFor(ka);
      const bm = etaFor(kb);
      return (am == null ? 1 : am) - (bm == null ? 1 : bm);
    })
    .map(([, d]) => d);
  return {
    hub: displayVessel(hubRaw),
    feeders: [...feeders.values()],
    destinations,
  };
}

/** Feeder → current vessel, with destination chips. No container prose. */
export function buildFeederFlows(shipment) {
  const groups = Array.isArray(shipment?.tracked_groups) ? shipment.tracked_groups : [];
  const nowKey = String(shipment?.current_vessel || shipment?.vessel || '').trim().toUpperCase();
  const buckets = new Map();
  groups.forEach((g) => {
    const feeder = String(g?.booking_vessel || '').trim();
    if (!feeder) return;
    if (feeder.toUpperCase() === nowKey) return;
    const fk = feeder.toUpperCase();
    if (!buckets.has(fk)) {
      buckets.set(fk, { feeder: displayVessel(feeder), stops: new Map() });
    }
    const portRaw = String(g?.arrival_port || '').trim();
    const pk = portRaw.toUpperCase() || '_';
    const stop = buckets.get(fk).stops.get(pk) || {
      port: shortPort(portRaw).replace(/^Port /i, ''),
      n: 0,
      delay: null,
      heads: [],
    };
    const boxes = Number(g?.total_containers);
    stop.n += Number.isFinite(boxes) ? boxes : 0;
    const delay = Number(g?.delay_days ?? g?.delayDays);
    if (Number.isFinite(delay) && (stop.delay == null || delay > stop.delay)) {
      stop.delay = delay;
    }
    const head = String(g?.container_number || g?.tracking_container || '').trim();
    if (head && !stop.heads.includes(head)) stop.heads.push(head);
    buckets.get(fk).stops.set(pk, stop);
  });
  return [...buckets.values()].map((b) => ({
    feeder: b.feeder,
    stops: [...b.stops.values()],
  }));
}

/** One short audit sentence for a single cargo group. API-only names. */
export function formatVesselAuditParagraph(shipment) {
  const stored = asJsonArray(shipment?.audit_paragraphs);
  if (stored.length === 1 && stored[0]) return String(stored[0]);

  const audit = asJsonArray(shipment?.vessel_audit);
  const prev = [];
  const next = [];
  const seen = new Set();
  let current = '';
  audit.forEach((a) => {
    const n = String(a?.vessel_name || '').trim();
    if (!n || n.toUpperCase() === 'RAILWAY') return;
    const role = String(a?.role || '');
    const key = n.toUpperCase();
    if (role === 'next') {
      if (!seen.has(`n:${key}`)) {
        seen.add(`n:${key}`);
        next.push(displayVessel(n));
      }
      return;
    }
    if (role === 'current') {
      current = n;
      return;
    }
    if (seen.has(key)) return;
    seen.add(key);
    prev.push(displayVessel(n));
  });
  const now = displayVessel(current || shipment?.current_vessel || shipment?.vessel || '');
  const was = prev.length
    ? prev.join(', ')
    : displayVessel(shipment?.booking_vessel || '');
  const then = next.join(', ') || displayVessel(shipment?.next_vessel || '');
  const n = Number(shipment?.total_containers);
  const boxes = Number.isFinite(n) && n > 0 ? n : 0;
  if (!was) return null;
  if (now && was.toUpperCase() === now.toUpperCase() && !(then && then.toUpperCase() !== now.toUpperCase())) {
    return null;
  }
  const bits = [boxes ? `${boxes} from ${was}` : `From ${was}`];
  const head = String(shipment?.container_number || shipment?.tracking_container || '').trim();
  if (head) bits.push(head);
  if (then && now && then.toUpperCase() !== now.toUpperCase()) bits.push(`then ${then}`);
  const delay = Number(shipment?.delay_days ?? shipment?.delayDays);
  if (Number.isFinite(delay) && delay >= 1) bits.push(`delayed +${Math.round(delay)}d`);
  else if (Number.isFinite(delay) && delay <= -1) bits.push(`ahead ${Math.round(Math.abs(delay))}d`);
  return bits.join(' · ');
}

/** One paragraph per feeder group when several boarded the same ship. */
export function formatVesselAuditParagraphs(shipment) {
  const stored = asJsonArray(shipment?.audit_paragraphs)
    .map((p) => String(p || '').trim())
    .filter(Boolean);
  if (stored.length) return stored;
  const one = formatVesselAuditParagraph(shipment);
  return one ? [one] : [];
}

/** Latest vs previous ocean vessels. Prefer BQ vessel_audit. */
export function formatShipChangeLine(shipment) {
  const audit = asJsonArray(shipment?.vessel_audit);
  if (audit.length) {
    const prev = [];
    const next = [];
    const seen = new Set();
    let current = '';
    audit.forEach((a) => {
      const n = String(a?.vessel_name || '').trim();
      if (!n || n.toUpperCase() === 'RAILWAY') return;
      const role = String(a?.role || '');
      const key = n.toUpperCase();
      if (role === 'next') {
        if (!seen.has(`n:${key}`)) {
          seen.add(`n:${key}`);
          next.push(displayVessel(n));
        }
        return;
      }
      if (role === 'current') {
        current = n;
        return;
      }
      if (seen.has(key)) return;
      seen.add(key);
      prev.push(displayVessel(n));
    });
    const now = displayVessel(current || shipment?.current_vessel || '');
    const was = prev.join(' / ');
    if (next.length || shipment?.next_vessel) {
      return 'Ship change in Panama';
    }
    if (was && now && was.toUpperCase() !== now.toUpperCase()) {
      return `Was ${was}, now ${now}`;
    }
    return null;
  }
  const changes = parseShipChanges(shipment);
  if (!changes.length) return null;
  const change = changes[changes.length - 1];
  const port = shortPort(change.at_port);
  const date = formatEtaDate(change.at_date);
  const was = displayVessel(change.from_vessel || '');
  const now = displayVessel(shipment?.current_vessel || shipment?.vessel || change.to_vessel);
  if (!port && !was && !now) return null;
  const where = [port, date].filter(Boolean).join(' · ');
  const who = was && now
    ? `was ${was}, now ${now}`
    : now ? `now ${now}` : `was ${was}`;
  return where ? `Ship change at ${where} — ${who}` : `Ship change — ${who}`;
}


function portKey(port) {
  return String(port || '').toUpperCase().replace(/[^A-Z]/g, ' ').replace(/\s+/g, ' ').trim();
}

function cargoPodStops(shipment) {
  const seen = new Map();
  const stops = asJsonArray(shipment?.voyage_stops);
  const uniquePods = new Set(stops.map((s) => portKey(s?.arrival_port)).filter(Boolean));
  const selfPod = portKey(shipment?.arrival_port);
  const allowAll = uniquePods.size > 1;
  stops.forEach((stop) => {
    const port = stop?.arrival_port;
    if (!port) return;
    const key = portKey(port);
    if (!key) return;
    if (!allowAll && selfPod && key !== selfPod) return;
    if (!seen.has(key)) seen.set(key, stop);
  });
  return [...seen.values()].sort((a, b) => {
    const am = toDayMs(predictedEta(a));
    const bm = toDayMs(predictedEta(b));
    return (am == null ? 1 : am) - (bm == null ? 1 : bm);
  });
}

function chainBars(shipment, rows, fromLegs) {
  const items = rows.map((row) => ({
    key: row.key,
    port: row.port,
    country: row.country,
    leftRaw: row.leftRaw || shipment?.etd || null,
    rightRaw: row.rightRaw,
    delay_days: row.delay_days,
    next_vessel: row.next_vessel,
    boxes: row.boxes,
    kg: row.kg,
    commodities: row.commodities,
    transit_percentage: row.transit_percentage,
    status: row.status,
    discharged: Boolean(row.discharged),
    fullyDischarged: Boolean(row.fullyDischarged),
  }));
  items.fromLegs = fromLegs;
  return items;
}

/**
 * Prefer API voyage_stops cargo PODs (USA destinations with cargo).
 * vessel_legs only when there are no cargo PODs — never prepend Balboa/Rodman
 * as Stop 1 on a Glacier / multi-POD card.
 */
export function buildProgressBars(shipment) {
  const pods = cargoPodStops(shipment);
  if (pods.length >= 1) {
    const rows = pods.map((stop, idx) => {
      const port = stop?.arrival_port || '—';
      const groups = Array.isArray(shipment?.tracked_groups) ? shipment.tracked_groups : [];
      const samePod = groups.filter((g) => portKey(g?.arrival_port) === portKey(port));
      const discharged = cargoIsDischarged(stop)
        || (samePod.length > 0 && samePod.every(cargoIsDischarged));
      const fullyDischarged = samePod.length > 0
        ? samePod.every(cargoIsFullyDischarged)
        : cargoIsFullyDischarged(stop);
      return {
        key: stop?.container_number || `${stop?.arrival_port || 'pod'}-${idx}`,
        port,
        country: stop?.destination_country,
        leftRaw: stop?.etd || shipment?.etd || null,
        rightRaw: predictedEta(stop) || predictedEta(shipment),
        delay_days: stop?.delay_days ?? (pods.length === 1 ? shipment?.delay_days : null),
        next_vessel: stop?.next_vessel || '',
        boxes: stop?.total_containers,
        kg: stop?.total_weight,
        commodities: stop?.commodities || '',
        transit_percentage: stop?.transit_percentage,
        status: stop?.status || (discharged ? 'arrived' : ''),
        discharged,
        fullyDischarged,
      };
    });
    return chainBars(shipment, rows, false);
  }

  const legs = parseVesselLegs(shipment);
  if (legs.length < 2) return null;
  const changes = parseShipChanges(shipment);
  const rows = legs.map((leg, idx) => {
    const isLast = idx === legs.length - 1;
    const port = leg.arrival_port || '—';
    let rightRaw = null;
    if (isLast) {
      rightRaw = predictedEta(shipment);
    } else {
      const portU = String(port).toUpperCase();
      const next = legs[idx + 1];
      const byPort = changes.find((c) => String(c.at_port || '').toUpperCase() === portU);
      const byVessel = !byPort && next
        ? changes.find((c) => String(c.to_vessel || '') === String(next.vessel_name || ''))
        : null;
      rightRaw = (byPort || byVessel)?.at_date || next?.event_date || null;
    }
    return {
      key: `${leg.imo || leg.vessel_name || 'leg'}-${idx}`,
      port,
      rightRaw,
      delay_days: isLast ? shipment?.delay_days : null,
    };
  });
  return chainBars(shipment, rows, true);
}
